// HG brand assets and certification rungs for the Brand & Marketing page.
//
// The copy blocks themselves are NOT here -- they are generated verbatim from the docx into
// marketingKitCopy.js and re-exported below, so that no hand-edit can drift them.

import {
  COPY_BLOCKS as KIT_BLOCKS, PUBLISH_CHECKLIST as KIT_CHECKLIST, RULES_IN_ONE_BREATH as KIT_RULES,
} from "./marketingKitCopy";

// A FITTER DOES NOT READ HIS NAME IN THE APP, WHATEVER THE KIT DOCUMENT SAYS.
//
// The kit document (the docx) tells a fitter to send his pages "to Randall". marketingKitCopy.js is generated from
// that document, so re-running the generator brings the name straight back -- and on 2 October two such lines were
// fixed by hand in the generated file, which the next regeneration would have undone. The fix belongs HERE, in the
// layer the generator does not write: every kit string passes through fitterFacing() on its way to the page, so a
// regenerated file cannot put his name in front of a fitter. brandKit.test.js runs it over the real copy.
//
// It rewrites the person to the company and nothing else. If the document is ever changed to say Henry-Griffitts,
// this becomes a no-op and can stay.
export const fitterFacing = (s) => String(s).replace(/\bRandall(?: Henry)?\b/g, "Henry-Griffitts");

export const COPY_BLOCKS = KIT_BLOCKS.map((b) => ({ ...b, title: fitterFacing(b.title), text: fitterFacing(b.text) }));
export const PUBLISH_CHECKLIST = KIT_CHECKLIST.map(fitterFacing);
export const RULES_IN_ONE_BREATH = fitterFacing(KIT_RULES);

// The three rungs.
//
// THESE NOW MATCH THE BACKEND. Rung 1 read "Certified Clubfitter" in CERT_LEVELS while the
// guide's rule 2 and the badge artwork both said "Certified Fitter"; Randall ruled for the
// guide on 2026-09-02 and backend/education.py was corrected, so the two agree and this table
// is no longer a local override of anything.
//
// It is still duplicated here rather than fetched, because these strings pick the BADGE FILE
// as well as the wording, and a page that cannot name its own assets offline is worse than
// one that repeats three nouns. education.display_for() is the authority for what a
// CERTIFICATE says; this is the authority for what a fitter writes about themselves. If the
// rungs are ever renamed again, both move.
export const RUNGS = {
  certified:    { key: "certified",    rung: 1, title: "Certified Fitter",         badge: "hg-badge-certified-fitter" },
  professional: { key: "professional", rung: 2, title: "Professional Clubfitter®", badge: "hg-badge-professional-clubfitter" },
  master:       { key: "master",       rung: 3, title: "Master Clubfitter™",  badge: "hg-badge-master-clubfitter" },
};

export const FALLBACK_RUNG = RUNGS.certified;

const CLOUDINARY = "https://res.cloudinary.com/dflzi5xc3/image/upload";

export const GUIDE_DOCX_URL =
  "https://res.cloudinary.com/dflzi5xc3/raw/upload/v1788307503/hg-brand/docs/HG_Fitter_CoMarketing_Guide_and_Kit_2026-09.docx";
export const GUIDELINES_PDF_URL =
  `${CLOUDINARY}/v1788307629/hg-brand/docs/HG_Brand_Guidelines_V3_2026-09.pdf`;

// public_id@version, never a bare name: the same public_id at a different version is a
// different asset, and pinning the name is not pinning the thing.
const BADGE_VERSIONS = {
  "hg-badge-certified-fitter-charcoal": 1788307615,
  "hg-badge-certified-fitter-white": 1788307617,
  "hg-badge-professional-clubfitter-charcoal": 1788307620,
  "hg-badge-professional-clubfitter-white": 1788307622,
  "hg-badge-master-clubfitter-charcoal": 1788307624,
  "hg-badge-master-clubfitter-white": 1788307627,
};

export function badgeUrl(badge, variant) {
  const id = `${badge}-${variant}`;
  const v = BADGE_VERSIONS[id];
  if (!v) throw new Error(`no pinned version for badge ${id}`);
  return `${CLOUDINARY}/v${v}/hg-brand/badges/${id}.png`;
}

export const LOGOS = [
  { label: "Full color",         id: "hg-logo-full-colour",      svg: 1788307475, png: 1788307594 },
  { label: "Reversed",           id: "hg-logo-reversed",         svg: 1788307483, png: 1788307597 },
  { label: "One color — black", id: "hg-logo-one-colour-black", svg: 1788307486, png: 1788307600 },
  { label: "One color — white", id: "hg-logo-one-colour-white", svg: 1788307489, png: 1788307602 },
  { label: "One color — green", id: "hg-logo-one-colour-green", svg: 1788307492, png: 1788307605 },
];

// The mark for a DARK ground: green tree, white wordmark. Named rather than indexed so the
// page's own header cannot drift onto a variant whose wordmark vanishes into the background.
export const HEADER_MARK = LOGOS.find((l) => l.id === "hg-logo-reversed");

export const TREES = [
  { label: "Tree — green", id: "hg-tree-green", svg: 1788307299, png: 1788307610 },
  { label: "Tree — black", id: "hg-tree-black", svg: 1788307496, png: 1788307608 },
  { label: "Tree — white", id: "hg-tree-white", svg: 1788307499, png: 1788307612 },
];

export const logoSvgUrl = (a) => `${CLOUDINARY}/v${a.svg}/hg-brand/${a.id}.svg`;
export const logoPngUrl = (a) => `${CLOUDINARY}/v${a.png}/hg-brand/${a.id}-png.png`;

export const FOUR_RULES =
  "Four rules: use the tree, say exactly what you are, sell the fitting not the clubs, " +
  "show us before it goes live.";

// ---------------------------------------------------------------- the fitter's own rung
//
// WHY THIS RETURNS A WHOLE ANSWER AND NOT A RUNG. The rung drives which badge a fitter is
// shown and what [Your Title] becomes, so "I could not read it" and "there is none on record"
// must not collapse into the same value. They lead to different messages, and a request that
// FAILED is not an empty result -- so the caller is handed `known`, and cannot reach a
// confident answer without it.
//
// Note also where the rung actually lives: NOT on the user document. The user doc carries no
// certification field at all; the level is on the issued certificate, reachable through
// GET /api/education/certification/eligibility. So this is a join, not a field read.
export function readRung(eligibility, { failed = false } = {}) {
  if (failed) {
    return { known: false, reason: "unreadable", rung: FALLBACK_RUNG };
  }
  const cert = eligibility?.certificate;
  const level = cert?.level;
  if (!eligibility?.issued || !level || !RUNGS[level]) {
    return { known: false, reason: "not-on-record", rung: FALLBACK_RUNG };
  }
  return { known: true, reason: null, rung: RUNGS[level] };
}

// THE ONE PLACE THE SUBSTITUTION MAP IS BUILT.
//
// Exported so the page and its tests use the SAME map. An earlier version of the test built
// its own copy, which meant it could not catch the component drifting away from it -- a test
// standing next to the code rather than over it.
//
// ORDER MATTERS. The command defines [Your Title] as "Henry-Griffitts " + the rung name, but
// the kit's own sentences already write the brand in front of the placeholder in 7 of the 9
// places it appears, so the literal reading renders "a Henry-Griffitts Henry-Griffitts
// Certified Fitter". The prefixed form is therefore consumed FIRST and only a bare
// [Your Title] has the brand added. Both paths land on the guide's rule 2 -- the rung written
// in full with "Henry-Griffitts" in front of it -- exactly once.
export function buildPlaceholderValues({ name, rung, facility, city }) {
  const fullTitle = `Henry-Griffitts ${rung.title}`;
  return {
    "Henry-Griffitts [Your Title]": fullTitle,
    "[Your Title]": fullTitle,
    "[Your Name]": name,
    "[Facility]": facility,
    "[City]": city,
  };
}

// Substitute only what the record actually carries. A field the record does not hold stays
// BRACKETED rather than blanked or guessed -- the bracket is the fitter's instruction that
// this one is still theirs to write.
export function fillPlaceholders(text, values) {
  let out = text;
  for (const [token, value] of Object.entries(values || {})) {
    if (value === null || value === undefined || value === "") continue;
    out = out.split(token).join(value);
  }
  return out;
}
